import { useState, useEffect } from 'react';
import { getEntityChangeLog, getUsers } from '../lib/api';
import { Badge } from './ui/badge';
import { History, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { it } from 'date-fns/locale';

const CHANGE_TYPE_LABELS = {
  pm_assigned: { label: 'PM Assegnato', color: 'bg-green-100 text-green-800' },
  pm_removed: { label: 'PM Rimosso', color: 'bg-red-100 text-red-800' },
  venditore_assigned: { label: 'Operativo Assegnato', color: 'bg-orange-100 text-orange-800' },
  venditore_removed: { label: 'Operativo Rimosso', color: 'bg-orange-100 text-orange-800' },
  account_changed: { label: 'Account Cambiato', color: 'bg-purple-100 text-purple-800' },
  contract_changed: { label: 'Contratto Cambiato', color: 'bg-yellow-100 text-yellow-800' },
  client_changed: { label: 'Cliente Cambiato', color: 'bg-indigo-100 text-indigo-800' },
  function_changed: { label: 'Funzione Cambiata', color: 'bg-teal-100 text-teal-800' },
};

// Patterns per riconoscere UUID
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Mappa per tradurre i nomi dei campi e delle funzioni in italiano
const FIELD_LABELS = {
  user_id: 'Utente',
  function: 'Ruolo',
  ended_at: 'Terminato il',
};

const FUNCTION_LABELS = {
  sales_coach: 'Sales Coach Junior',
  sales_coach_senior: 'Sales Coach Senior',
  marketing_manager: 'Marketing Manager Junior',
  marketing_manager_senior: 'Marketing Manager Senior',
  hunter_manager: 'Hunter Manager',
  hunter: 'Hunter',
  hunter_senior: 'Hunter Senior',
  setter: 'Setter',
  copywriter: 'Copywriter',
  media_buyer: 'Media Buyer',
  supervisor_sales: 'Supervisor Sales',
  supervisor_sales_hunter: 'Supervisor Sales Hunter',
  supervisor_marketing: 'Supervisor Marketing',
  closer_manager: 'Closer Manager',
  venditore: 'Operativo',
  account: 'Account',
};

export default function EntityChangeLog({ entityType, entityId }) {
  const [logs, setLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (entityId) {
      loadData();
    }
  }, [entityType, entityId]);

  const loadData = async () => {
    try {
      const [logsRes, usersRes] = await Promise.all([
        getEntityChangeLog(entityType, entityId),
        getUsers()
      ]);
      setLogs(logsRes.data);
      setUsers(usersRes.data);
    } catch (err) {
      console.error('Error loading change log:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    try {
      return format(parseISO(dateStr), 'dd MMM yyyy HH:mm', { locale: it });
    } catch {
      return dateStr;
    }
  };

  const getChangeLabel = (changeType) => {
    return CHANGE_TYPE_LABELS[changeType] || { label: changeType, color: 'bg-gray-100 text-gray-800' };
  };

  // Risolve un UUID in nome utente se possibile
  const resolveUserId = (value) => {
    if (!value || typeof value !== 'string') return value;
    if (!UUID_PATTERN.test(value)) return value;
    
    const user = users.find(u => u.id === value);
    return user ? user.name : value;
  };

  // Formatta una data ISO in formato leggibile
  const formatDateTime = (isoStr) => {
    try {
      return format(parseISO(isoStr), 'dd MMM yyyy HH:mm', { locale: it });
    } catch {
      return isoStr;
    }
  };

  // Formatta il valore in modo intelligente
  const formatValue = (value) => {
    if (!value) return '-';
    
    // Gestione speciale per oggetti con user_id e function (es. assegnazione PM/Venditore)
    if (typeof value === 'object') {
      // Caso: assegnazione utente con ruolo { user_id: '...', function: '...' }
      if (value.user_id && value.function) {
        const userName = resolveUserId(value.user_id);
        const roleLabel = FUNCTION_LABELS[value.function] || value.function;
        return `${userName} (${roleLabel})`;
      }
      
      // Caso: rimozione con ended_at { ended_at: '...' }
      if (value.ended_at) {
        return `Terminato: ${formatDateTime(value.ended_at)}`;
      }
      
      // Fallback per altri oggetti
      return Object.entries(value)
        .map(([k, v]) => {
          const label = FIELD_LABELS[k] || k;
          const resolvedValue = typeof v === 'string' && UUID_PATTERN.test(v) 
            ? resolveUserId(v) 
            : v;
          return `${label}: ${resolvedValue}`;
        })
        .join(', ');
    }
    
    // Prova a risolvere il valore come user_id
    return resolveUserId(String(value));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-4">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (logs.length === 0) {
    return null; // Don't show anything if no logs
  }

  const displayLogs = expanded ? logs : logs.slice(0, 3);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <History className="w-4 h-4 text-gray-500" />
        <h4 className="text-sm font-medium text-gray-600">Log Modifiche</h4>
        <Badge variant="outline" className="text-xs">{logs.length}</Badge>
      </div>

      <div className="space-y-2">
        {displayLogs.map((log) => {
          const changeInfo = getChangeLabel(log.change_type);
          return (
            <div
              key={log.id}
              className="p-2 bg-gray-50 rounded-lg text-sm"
            >
              <div className="flex items-center justify-between mb-1">
                <Badge className={`${changeInfo.color} text-xs`}>
                  {changeInfo.label}
                </Badge>
                <span className="text-xs text-gray-500">
                  {formatDate(log.changed_at)}
                </span>
              </div>
              <div className="text-xs text-gray-600">
                <span className="font-medium">Da:</span> {log.changed_by_name}
              </div>
              {log.old_value && (
                <div className="text-xs text-gray-500 mt-1">
                  <span className="text-red-600">Vecchio:</span> {formatValue(log.old_value)}
                </div>
              )}
              <div className="text-xs text-gray-500">
                <span className="text-green-600">Nuovo:</span> {formatValue(log.new_value)}
              </div>
              {log.notes && (
                <div className="text-xs text-gray-500 mt-1 italic">
                  Note: {log.notes}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {logs.length > 3 && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-1 text-sm text-orange-600 hover:text-orange-800"
        >
          {expanded ? (
            <>
              <ChevronUp className="w-4 h-4" />
              Mostra meno
            </>
          ) : (
            <>
              <ChevronDown className="w-4 h-4" />
              Mostra altri {logs.length - 3} log
            </>
          )}
        </button>
      )}
    </div>
  );
}
